import { useState, useEffect, useCallback } from 'react';
import { 
  MapPin, Package, Phone, Calendar, Clock, User, TrendingUp, 
  CheckCircle, AlertCircle, Navigation, Truck, Milk, IndianRupee,
  ChevronDown, ChevronUp, Search, Filter, AlertTriangle
} from 'lucide-react';
import { deliveryBoyAPI, ordersAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import ItemDetailModal from '../components/ItemDetailModal';

const DeliveryManagement = () => {
  const { hasRole } = useAuth();
  const isAdmin = hasRole(['admin', 'super_admin', 'developer']);
  
  // State
  const [activeTab, setActiveTab] = useState(isAdmin ? 'all-orders' : 'my-orders');
  const [orders, setOrders] = useState([]);
  const [deliveryBoys, setDeliveryBoys] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [bulkAssignMode, setBulkAssignMode] = useState(false);
  const [selectedDeliveryBoy, setSelectedDeliveryBoy] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [itemDetailModalOpen, setItemDetailModalOpen] = useState(false);


  // Fetch data based on role and active tab
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      if (isAdmin && activeTab === 'all-orders') {
        // Fetch all orders for admin
        const ordersRes = await ordersAPI.getAll({ 
          status: statusFilter !== 'all' ? statusFilter : undefined 
        });
        setOrders(ordersRes.data.data);
        
        // Fetch delivery boys for assignment
        const deliveryBoysRes = await deliveryBoyAPI.getAllDeliveryBoys();
        setDeliveryBoys(deliveryBoysRes.data.data);
        
        // Fetch admin stats
        const statsRes = await deliveryBoyAPI.getAdminStats();
        setStats(statsRes.data.data);
      } else {
        // Fetch my orders (delivery boy view)
        const ordersRes = await deliveryBoyAPI.getMyOrders({ 
          status: statusFilter !== 'all' ? statusFilter : undefined 
        });
        setOrders(ordersRes.data.data);
        
        // Fetch personal stats
        const statsRes = await deliveryBoyAPI.getStats();
        setStats(statsRes.data.data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  }, [isAdmin, activeTab, statusFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle order assignment
  const handleAssignOrder = async (orderUuid, deliveryBoyUuid) => {
    try {
      await deliveryBoyAPI.assignOrder(orderUuid, deliveryBoyUuid);
      toast.success('Order assigned successfully');
      fetchData();
    } catch (error) {
      console.error('Error assigning order:', error);
      toast.error('Failed to assign order');
    }
  };

  // Handle bulk assignment
  const handleBulkAssign = async () => {
    if (!selectedDeliveryBoy) {
      toast.error('Please select a delivery boy');
      return;
    }

    if (selectedOrders.length === 0) {
      toast.error('Please select at least one order');
      return;
    }

    try {
      await Promise.all(
        selectedOrders.map(orderUuid => 
          deliveryBoyAPI.assignOrder(orderUuid, selectedDeliveryBoy)
        )
      );
      toast.success(`${selectedOrders.length} orders assigned successfully`);
      setSelectedOrders([]);
      setBulkAssignMode(false);
      setSelectedDeliveryBoy('');
      fetchData();
    } catch (error) {
      console.error('Error bulk assigning orders:', error);
      toast.error('Failed to assign some orders');
    }
  };

  // Handle status update
  const handleStatusUpdate = async (orderUuid, newStatus) => {
    try {
      await deliveryBoyAPI.updateOrderStatus(orderUuid, newStatus);
      toast.success('Order status updated');
      fetchData();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  // Handle complete delivery
  const handleCompleteDelivery = async (orderUuid, notes = '') => {
    try {
      await deliveryBoyAPI.completeDelivery(orderUuid, notes);
      toast.success('Order marked as delivered');
      fetchData();
    } catch (error) {
      console.error('Error completing delivery:', error);
      toast.error('Failed to complete delivery');
    }
  };



  // Filter orders based on search
  const filteredOrders = orders.filter(order => {
    const searchLower = searchTerm.toLowerCase();
    return (
      order.orderNumber?.toLowerCase().includes(searchLower) ||
      order.customerName?.toLowerCase().includes(searchLower) ||
      order.customerPhone?.includes(searchTerm) ||
      order.deliveryAddress?.toLowerCase().includes(searchLower)
    );
  });

  // Toggle order selection for bulk actions
  const toggleOrderSelection = (orderUuid) => {
    setSelectedOrders(prev => 
      prev.includes(orderUuid) 
        ? prev.filter(id => id !== orderUuid)
        : [...prev, orderUuid]
    );
  };

  // Status badge component
  const StatusBadge = ({ status }) => {
    const statusStyles = {
      pending: 'bg-yellow-100 text-yellow-800',
      confirmed: 'bg-blue-100 text-blue-800',
      preparing: 'bg-purple-100 text-purple-800',
      out_for_delivery: 'bg-indigo-100 text-indigo-800',
      delivered: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
    };

    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusStyles[status] || 'bg-gray-100 text-gray-800'}`}>
        {status?.replace('_', ' ').toUpperCase()}
      </span>
    );
  };

  // Statistics Cards Component
  const StatsCards = () => {
    if (!stats) return null;

    const todayStats = stats.today || stats;

    return (
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Orders</p>
              <p className="text-2xl font-bold text-gray-900">{todayStats.total || todayStats.totalDeliveries || 0}</p>
            </div>
            <Package className="w-10 h-10 text-blue-500" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Delivered</p>
              <p className="text-2xl font-bold text-green-600">{todayStats.delivered || todayStats.completedDeliveries || 0}</p>
            </div>
            <CheckCircle className="w-10 h-10 text-green-500" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">In Progress</p>
              <p className="text-2xl font-bold text-indigo-600">{todayStats.inProgress || todayStats.pendingDeliveries || 0}</p>
            </div>
            <Truck className="w-10 h-10 text-indigo-500" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Pending</p>
              <p className="text-2xl font-bold text-yellow-600">{todayStats.pending || 0}</p>
            </div>
            <AlertCircle className="w-10 h-10 text-yellow-500" />
          </div>
        </div>
      </div>
    );
  };

  // Order Card Component
  const OrderCard = ({ order }) => {
    const isExpanded = expandedOrder === order.uuid;
    const isSelected = selectedOrders.includes(order.uuid);

    return (
      <div className={`bg-white rounded-lg shadow-sm border-2 transition-all ${
        isSelected ? 'border-blue-500' : 'border-gray-200'
      }`}>
        {/* Order Header */}
        <div className="p-4">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              {bulkAssignMode && (
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => toggleOrderSelection(order.uuid)}
                  className="mt-1 w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                />
              )}
              
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="font-semibold text-lg text-gray-900">
                    {order.orderNumber}
                  </h3>
                  <StatusBadge status={order.status} />
                  {order.orderType === 'subscription' && (
                    <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full font-medium">
                      SUBSCRIPTION
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center gap-2 text-gray-700">
                    <User className="w-4 h-4" />
                    <span className="font-medium">{order.customerName}</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-700">
                    <Phone className="w-4 h-4" />
                    <span>{order.customerPhone}</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-700">
                    <Calendar className="w-4 h-4" />
                    <span>{new Date(order.deliveryDate).toLocaleDateString()}</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-700">
                    <Clock className="w-4 h-4" />
                    <span className="capitalize">{order.deliveryTimeSlot?.replace('_', ' ')}</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-700">
                    <IndianRupee className="w-4 h-4" />
                    <span className="font-semibold">₹{(parseFloat(order.totalAmount) || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-700">
                    <Package className="w-4 h-4" />
                    <span className="text-xs uppercase">{order.paymentMethod} - {order.paymentStatus}</span>
                  </div>
                </div>

                {order.deliveryAddress && (
                  <div className="flex items-start gap-2 text-gray-700 mt-3 text-sm">
                    <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>{order.deliveryAddress}</span>
                  </div>
                )}
              </div>
            </div>

            <button
              onClick={() => setExpandedOrder(isExpanded ? null : order.uuid)}
              className="ml-4 p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Expanded Content */}
        {isExpanded && (
          <div className="border-t border-gray-200 p-4 bg-gray-50 space-y-4">
            {/* Action Buttons */}
            <div className="flex flex-wrap gap-2">
              {order.navigationUrl && (
                <a
                  href={order.navigationUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Navigation className="w-4 h-4" />
                  Navigate
                </a>
              )}

              {!isAdmin && order.status === 'confirmed' && (
                <button
                  onClick={() => handleStatusUpdate(order.uuid, 'out_for_delivery')}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  <Truck className="w-4 h-4" />
                  Start Delivery
                </button>
              )}

              {!isAdmin && order.status === 'out_for_delivery' && (
                <button
                  onClick={() => handleCompleteDelivery(order.uuid)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <CheckCircle className="w-4 h-4" />
                  Mark Delivered
                </button>
              )}



              {isAdmin && !order.assignedDeliveryBoyUuid && (
                <select
                  onChange={(e) => handleAssignOrder(order.uuid, e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  defaultValue=""
                >
                  <option value="" disabled>Assign to...</option>
                  {deliveryBoys.map(boy => (
                    <option key={boy.uuid} value={boy.uuid}>
                      {boy.name} {!boy.isAvailable && '(Busy)'}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Bottle Information */}
            {(order.bottlesGiven > 0 || order.bottlesReturned > 0 || order.bottlesBroken > 0) && (
              <div className="bg-white p-4 rounded-lg border border-purple-200">
                <div className="flex items-center gap-2 mb-3">
                  <Milk className="w-5 h-5 text-purple-600" />
                  <h4 className="font-semibold text-gray-900">Bottle Tracking</h4>
                </div>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600">Given</p>
                    <p className="font-semibold text-green-600">{order.bottlesGiven || 0}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Returned</p>
                    <p className="font-semibold text-blue-600">{order.bottlesReturned || 0}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Broken</p>
                    <p className="font-semibold text-red-600">{order.bottlesBroken || 0}</p>
                  </div>
                </div>
                {order.brokenBottleCharge > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Broken Bottle Charge:</span>
                      <span className="font-semibold text-red-600">₹{order.brokenBottleCharge}</span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Order Items */}
            {order.items && order.items.length > 0 && (
              <div className="bg-white p-4 rounded-lg border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-3">Order Items</h4>
                <div className="space-y-2">
                  {order.items.map(item => (
                    <button
                      key={item.uuid}
                      onClick={() => {
                        setSelectedItem(item);
                        setItemDetailModalOpen(true);
                      }}
                      className="w-full flex justify-between items-center text-sm p-2 hover:bg-gray-50 rounded transition-colors cursor-pointer"
                    >
                      <div className="text-gray-700 text-left flex-1">
                        <span>{item.productName}</span>
                        {item.variant && <span className="text-xs font-semibold text-blue-600 ml-1">{item.variant}</span>}
                        <span> x {item.quantity}</span>
                      </div>
                      <span className="font-semibold text-gray-900">₹{item.subtotal}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };



  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Delivery Management</h1>
        <p className="text-gray-600">
          {isAdmin ? 'Manage all delivery orders and assignments' : 'View and manage your assigned deliveries'}
        </p>
      </div>

      {/* Tabs (if admin) */}
      {isAdmin && (
        <div className="mb-6 border-b border-gray-200">
          <div className="flex gap-4">
            <button
              onClick={() => setActiveTab('all-orders')}
              className={`px-4 py-3 font-medium transition-colors border-b-2 ${
                activeTab === 'all-orders'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              All Orders
            </button>
          </div>
        </div>
      )}

      {/* Statistics */}
      <StatsCards />

      {/* Filters and Actions */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by order number, customer name, phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Status Filter */}
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Status</option>
              <option value="confirmed">Confirmed</option>
              <option value="preparing">Preparing</option>
              <option value="out_for_delivery">Out for Delivery</option>
              <option value="delivered">Delivered</option>
            </select>
          </div>

          {/* Bulk Assignment Toggle (Admin only) */}
          {isAdmin && activeTab === 'all-orders' && (
            <button
              onClick={() => {
                setBulkAssignMode(!bulkAssignMode);
                setSelectedOrders([]);
              }}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                bulkAssignMode
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {bulkAssignMode ? 'Cancel Bulk Mode' : 'Bulk Assign'}
            </button>
          )}
        </div>

        {/* Bulk Assignment Bar */}
        {bulkAssignMode && selectedOrders.length > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-200 flex items-center gap-4">
            <span className="text-sm font-medium text-gray-700">
              {selectedOrders.length} order(s) selected
            </span>
            <select
              value={selectedDeliveryBoy}
              onChange={(e) => setSelectedDeliveryBoy(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select delivery boy...</option>
              {deliveryBoys.map(boy => (
                <option key={boy.uuid} value={boy.uuid}>
                  {boy.name} {!boy.isAvailable && '(Busy)'}
                </option>
              ))}
            </select>
            <button
              onClick={handleBulkAssign}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              Assign Selected
            </button>
          </div>
        )}
      </div>

      {/* Orders List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Loading orders...</p>
        </div>
      ) : filteredOrders.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Orders Found</h3>
          <p className="text-gray-600">
            {searchTerm ? 'Try adjusting your search criteria' : 'No orders available at the moment'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredOrders.map(order => (
            <OrderCard key={order.uuid} order={order} />
          ))}
        </div>
      )}

      {/* Item Detail Modal */}
      <ItemDetailModal
        isOpen={itemDetailModalOpen}
        item={selectedItem}
        onClose={() => setItemDetailModalOpen(false)}
      />
    </div>
  );
};

export default DeliveryManagement;